import { roleDepth, unstopped, type Account, type Role } from '@/data/types'
import { STOP_COPY } from '@/lib/status'
import { paiseToDisplay, parseCurrencyType, type CurrencyType } from '@/lib/currency'
import { labRateFromShares, type LabRateRow } from '@/api/map/lab-rate'

/** Hierarchy AgentNode / AgentNodeWithBalance (OpenAPI). */
export interface AgentNode {
  id: string
  parentId: string | null
  name: string
  level: number
  creditLimit: string
  shareBp: number
  /** Casino partnership share (bp). Present on hierarchy AgentNode. */
  shareCasinoBp?: number
  commissionBp?: number
  /** Lab Member.bookmakerCommission (bp). Distinct from fancy commissionBp. */
  commissionBookmakerBp?: number
  /** Lab Member.rate[] — Info column; hierarchy synthesizes from share_* . */
  rate?: LabRateRow[]
  tbAccountId?: string
  balance?: string
  /** PT-share open liability (minor). Null when projector stale. */
  exposure?: string | null
  exposureStale?: boolean
  available?: string | null
  creditRemaining?: string | null
  liveBalance?: string
  totalBalance?: string
  distributedBalance?: string
  remainingToAssign?: string
  /** Lab balanceUp / Winnings (signed settlement P&L, minor). */
  postedPnl?: string
  currencyType?: string
  /** Lab casinoCurrencyType — Evolution HKD|AED. */
  casinoCurrencyType?: string
  /** Console username when the mock (or service) stores it. */
  username?: string
  /** Keycloak email-as-username when login was provisioned. */
  loginEmail?: string
  loginReady?: boolean
  /** Backend-UI Active / Inactive / Closed (plus suspended). */
  status?: 'active' | 'inactive' | 'suspended' | 'closed'
  /** Free-text note from Edit Member. */
  comments?: string
  /**
   * Winner7 downline max credit — level → minor units.
   * e.g. { "5": "600000", "6": "500000" }.
   */
  downlineMaxCredit?: Record<string, string>
  games?: import('@/api/map/gameLock').GameFlags
  gameLocks?: import('@/api/map/gameLock').GameLocks
}

/** Minor-unit string → major units (₹). */
export function fromMinorUnits(minor: string | null | undefined): number {
  if (minor == null || minor === '') return 0
  const n = Number(minor)
  if (!Number.isFinite(n) || n < 0) return 0
  return n / 100
}

/** Like fromMinorUnits but preserves unknown exposure as null. */
export function fromMinorUnitsOrNull(minor: string | null | undefined): number | null {
  if (minor === null) return null
  if (minor === undefined || minor === '') return 0
  const n = Number(minor)
  if (!Number.isFinite(n) || n < 0) return null
  return n / 100
}

/** OpenAPI level 1…6 → console Role (Winner7 ladder labels). */
export function levelToRole(level: number): Role {
  const map: Record<number, Role> = {
    1: 'company-master',
    2: 'triple-master',
    3: 'double-master',
    4: 'super-master',
    5: 'master',
    6: 'player',
  }
  return map[level] ?? 'master'
}

/** Console Role → hierarchy level (1=CM … 6=member). */
export function roleToLevel(role: Role): number {
  if (role === 'player') return 6
  return roleDepth(role) + 1
}

/** Map hierarchy level-keyed paise map → role-keyed display amounts. */
export function downlineMaxCreditFromNode(
  raw: Record<string, string> | undefined,
  currencyType: CurrencyType,
): Partial<Record<Role, number>> | undefined {
  if (!raw || typeof raw !== 'object') return undefined
  const out: Partial<Record<Role, number>> = {}
  for (const [levelStr, minor] of Object.entries(raw)) {
    const level = Number(levelStr)
    if (!Number.isInteger(level) || level < 1 || level > 6) continue
    const display = paiseToDisplay(String(minor), currencyType)
    if (!Number.isFinite(display) || display < 1) continue
    out[levelToRole(level)] = display
  }
  return Object.keys(out).length > 0 ? out : undefined
}

/** Map hierarchy node → console Account (fields the Create wizard needs). */
export function agentNodeToAccount(
  node: AgentNode,
  parent?: Pick<AgentNode, 'shareBp' | 'shareCasinoBp'> | null,
): Account {
  const currencyType = parseCurrencyType(node.currencyType)
  const sharePoints =
    currencyType === 'INR' ? (node.shareBp ?? 0) / 10_000 : (node.shareBp ?? 0) / 100
  const shareCasinoPoints = (node.shareCasinoBp ?? node.shareBp ?? 0) / 100
  const role = levelToRole(node.level)
  const share =
    currencyType === 'INR' && role === 'player'
      ? { sportsbook: 1, casino: 100 }
      : { sportsbook: sharePoints, casino: shareCasinoPoints }
  const commissionPct = (node.commissionBp ?? 0) / 100
  // Lab: bookmaker % is its own field; fall back to fancy only when API omits it.
  const bookmakerPct =
    node.commissionBookmakerBp != null ? node.commissionBookmakerBp / 100 : commissionPct
  const username = node.username?.trim() || node.name
  const status = node.status ?? 'active'
  const comments = node.comments?.trim() ? node.comments.trim() : undefined
  const exposureStale = node.exposureStale === true || node.exposure === null
  // Lab settlement from parent POV: positive = Take (child losses), negative = Give (wins).
  const postedPnlDisplay =
    node.postedPnl != null && String(node.postedPnl).trim() !== ''
      ? paiseToDisplay(node.postedPnl, currencyType)
      : 0
  const shareCasinoBp = node.shareCasinoBp ?? node.shareBp ?? 0
  const rate =
    Array.isArray(node.rate) && node.rate.length > 0
      ? node.rate
      : labRateFromShares({
          nodeId: node.id,
          shareBp: node.shareBp ?? 0,
          shareCasinoBp,
          parent: parent
            ? {
                shareBp: parent.shareBp ?? 0,
                shareCasinoBp: parent.shareCasinoBp ?? parent.shareBp ?? 0,
              }
            : null,
        })
  const downlineMaxCredit = downlineMaxCreditFromNode(node.downlineMaxCredit, currencyType)
  return {
    id: node.id,
    username,
    displayName: node.name,
    role,
    parentId: node.parentId,
    path: node.parentId ? [node.parentId] : [],
    status,
    createdAt: new Date().toISOString(),
    lastActiveAt: null,
    currencyType,
    casinoCurrencyType:
      String(node.casinoCurrencyType ?? '')
        .trim()
        .toUpperCase() === 'AED'
        ? 'AED'
        : 'HKD',
    creditLimit: paiseToDisplay(node.creditLimit, currencyType),
    ...(downlineMaxCredit ? { downlineMaxCredit } : {}),
    balance: paiseToDisplay(node.liveBalance ?? node.balance, currencyType),
    distributedBalance: paiseToDisplay(node.distributedBalance, currencyType),
    // Soft 0 when projector stale — never show a leftover fake non-zero from Redis.
    exposure: exposureStale ? 0 : paiseToDisplay(node.exposure ?? '0', currencyType),
    exposureStale,
    settlement: postedPnlDisplay === 0 ? 0 : -postedPnlDisplay,
    share,
    commission: { fancy: commissionPct, bookmaker: bookmakerPct, matchOdds: commissionPct },
    rate,
    betLimits: { min: 100, max: 100_000 },
    games: {
      casino: node.games?.casino !== false,
      cardGame: node.games?.cardGame !== false,
      liveGame: node.games?.liveGame !== false,
      sportsbook: node.games?.sportsbook !== false,
    },
    gameFlags: node.games,
    gameLocks: node.gameLocks ?? {},
    childCount: 0,
    memberCount: 0,
    flags: [],
    comments,
    // The hierarchy service keeps no suspensions, so the honest projection is
    // the node's own status and no control at all.
    ...unstopped(status, STOP_COPY.notWiredLive),
  }
}

/**
 * Backend-UI create max: remaining credit line =
 * parent.creditLimit − Σ direct children's creditLimits.
 */
export function allocatedCreditFromTree(
  parentId: string,
  nodes: Pick<AgentNode, 'parentId' | 'creditLimit' | 'currencyType'>[],
  parentCurrency: CurrencyType = 'INR',
): number {
  let allocatedPaise = 0
  for (const node of nodes) {
    if (node.parentId !== parentId) continue
    const n = Number(node.creditLimit ?? 0)
    if (Number.isFinite(n) && n > 0) allocatedPaise += n
  }
  return paiseToDisplay(allocatedPaise, parentCurrency)
}
